import Service from 'additiv-services/services/core/service';
import { getQueryStringByObject } from 'additiv-services/utils/queryUtils';
import { APIVersion } from 'additiv-services';
import { getCustomServiceLayerHeader } from 'additiv-services/setup';
import {
    CUSTOM_SERVICE_LAYER_SERVICES,
    getCustomServiceLayerBaseUrl,
} from '../constants';
import Security from '../../additiv-services/services/core/security';

class CustomDocumentService extends Service {
    static #baseServiceUrl = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.DOCUMENTS,
    );

    static #baseServiceUrlv11 = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.DOCUMENTS,
        APIVersion.V11,
    );

    static #baseServiceUrlv12 = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.DOCUMENTS,
        APIVersion.V12,
    );

    static #baseServiceUrlv10Contacts = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.CONTACTS,
        APIVersion.V10,
    );

    static #baseServiceUrlv10BundledDocuments = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.BUNDLE_DOCUMENTS,
        APIVersion.V10,
    );

    static getDocumentsByContainer({ containerId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/investment/container/${containerId}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getDocumentsByContainerWithNewAPI({ contactId, containerId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv10Contacts}/${contactId}/container/${containerId}/documents`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getDocumentsByInvestmentApplicationId({
        investmentApplicationId,
        contactId,
        language,
    }) {
        const queryString = getQueryStringByObject({ contactId });

        return Service.getRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getDocumentsByInvestmentApplicationIdWithNewAPI({
        investmentApplicationId,
        contactId,
        language,
    }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv10Contacts}/${contactId}/investments/${investmentApplicationId}/documents`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getDocumentsByContactId({ contactId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactId}/id-document`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getESignUrl({ documentId, returnUrl, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${documentId}/esign?returnUrl=${returnUrl}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static manualDocumentSign({ documentId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${documentId}/manual-sign`,
            rest,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static uploadProofOfAssetsDocuments(payload) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/proof-of-asset`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static postDocumentSentFlag({ documentId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${documentId}/is-sent`,
            {},
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static async getUnifiedDocuments(params) {
        const queryString = getQueryStringByObject(params);

        await Security.generateApplicationTokensIfNeeded();

        return Service.getRequest(
            `${this.#baseServiceUrl}/DocDB/GetUnifiedDocuments?${queryString}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: { 'accept-language': params?.language },
                },
            },
        );
    }

    static getDocumentPdf({ contactId, ...params }) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${this.#baseServiceUrlv10Contacts}/${contactId}/documents/pdf?${queryString}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: { 'accept-language': params?.language },
                },
            },
        );
    }

    static uploadIdDocument(data) {
        return Service.postRequest(`${this.#baseServiceUrl}/iddocument`, data, {
            isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static getGeneralDocumentsAdvisoryWay({ language, containerId }) {
        const queryString = getQueryStringByObject({ language });

        return Service.getRequest(
            `${this.#baseServiceUrl}/${containerId}/general-documents?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getGeneralDocuments(params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${this.#baseServiceUrl}/general-documents?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static uploadGeneralDocument({ payload, memberId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/general-documents?memberId=${memberId}`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static getOpeningLetter({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/portfolio/${portfolioId}/opening-letter`,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static getContactGroupDocuments({ contactGroupId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${contactGroupId}/contact-group`,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static uploadContactGroupDocuments({ payload }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/contact-group`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static getAdvisoryDocument({ advisoryDocumentId }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv11}/advisory-document/${advisoryDocumentId}`,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static addAdvisoryDocument({ payload }) {
        return Service.postRequest(
            `${this.#baseServiceUrlv11}/advisory-document`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static uploadAdvisoryDocument({ payload }) {
        return Service.postRequest(
            `${this.#baseServiceUrlv12}/advisory-document`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static upload3bApplicationFormDocuments(payload) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/application-form-document`,
            payload,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static reUploadAdvisoryDocument({
        advisoryId,
        containerId,
        file,
        filename,
    }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/advisory/${advisoryId}/file`,
            { containerId, file, filename },
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static deleteDocumentById({ documentId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/general-documents?dmsDocumentId=${documentId}`,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    static getQrPaymentSlip({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/portfolio/${portfolioId}/qr-bill`,
            { isSecure: true, axiosSetup: { headers: getCustomServiceLayerHeader() } },
        );
    }

    // Bundled Documents service methods
    static getBundledDocuments({ language }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv10BundledDocuments}/document-sets`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getAllStaticDocumentsInABundle({ bundleId, language }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv10BundledDocuments}/document-sets/${bundleId}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static uploadStaticDocumentsInABundle({ files, bundleDocumentIds, language }) {
        const formData = new FormData();

        (files ?? []).forEach((item, index) => {
            formData.append(`files[${index}].language`, item.language);
            formData.append(`files[${index}].file`, item.file, item.file?.name);
        });

        (bundleDocumentIds ?? []).forEach((id) => {
            formData.append('BundleDocumentIds', String(id));
        });

        return Service.postRequest(
            this.#baseServiceUrlv10BundledDocuments,
            formData,
            {
                isSecure: true,
                // Content-Type left undefined so the browser sets the multipart boundary.
                axiosSetup: { headers: { 'accept-language': language, 'Content-Type': undefined } },
            },
        );
    }

    static getAllStaticDocumentCategories({ language }) {
        return Service.getRequest(
            `${this.#baseServiceUrlv10BundledDocuments}/document-categories/static`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }
}

export default CustomDocumentService;
